<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { AlertTriangle, Check, Loader2, Mail, X } from 'lucide-svelte';

  import {
    placeExport,
    quoteExport,
    ExportRefused,
    type ExportQuote,
    type PlacedExport,
    type ExportRequest
  } from '$lib/api/accountorder';

  /**
   * Exporting a selection, for a signed-in customer.
   *
   * # Three states, one dialog
   *
   * Asking the price, showing it, and saying what happened. They are one thing
   * because they are one decision: a customer who sees a figure and then has to
   * find the button somewhere else has been asked to remember a number.
   *
   * # The price is asked for before anything is placed
   *
   * `POST /orders/quote` creates nothing, so opening this costs the customer
   * nothing and commits them to nothing. Placing is a separate call, and
   * **making it is the confirmation** — there is no flag distinguishing the
   * safe call from the dangerous one.
   *
   * # What follows depends on how the account is billed
   *
   * Postpaid delivers on confirmation and is invoiced afterwards. Prepaid gets
   * the invoice and the payment link by email and is delivered once paid. The
   * server decides which; this only reports it, because the mode is a
   * negotiated term rather than something the app should offer as a choice.
   */

  interface Props {
    /** What to export. Read once, when the dialog opens. */
    request: ExportRequest;
    onClose: () => void;
  }

  let { request, onClose }: Props = $props();

  let quote = $state<ExportQuote | null>(null);
  let placed = $state<PlacedExport | null>(null);
  let refusal = $state('');
  let asking = $state(true);
  let placing = $state(false);

  $effect(() => {
    const wanted = request;
    void quoteExport(wanted)
      .then((answer) => {
        quote = answer;
      })
      .catch((error) => {
        refusal = error instanceof ExportRefused ? error.message : $_('export.failed');
      })
      .finally(() => {
        asking = false;
      });
  });

  async function place() {
    if (placing || !quote) return;

    placing = true;
    refusal = '';
    try {
      placed = await placeExport(request);
    } catch (error) {
      refusal = error instanceof ExportRefused ? error.message : $_('export.failed');
    } finally {
      placing = false;
    }
  }

  const money = (amount: number) =>
    `${quote?.currency ?? 'CHF'} ${amount.toLocaleString('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  function onKeydown(event: KeyboardEvent) {
    // Escape closes while nothing is in flight. A placed export cannot be
    // taken back, so the dialog that says so is dismissed deliberately.
    if (event.key === 'Escape' && !placing) onClose();
  }
</script>

<svelte:window on:keydown={onKeydown} />

<div
  class="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm"
  role="presentation"
  onclick={() => !placing && onClose()}
>
  <div
    class="w-full max-w-lg rounded-surface border border-gray-200 bg-white shadow-overlay"
    role="dialog"
    aria-modal="true"
    aria-label={$_('export.title')}
    tabindex="-1"
    onclick={(event) => event.stopPropagation()}
  >
    <div class="flex items-center justify-between border-b border-gray-100 px-5 py-4">
      <h3 class="text-base font-semibold text-gray-900">
        {placed ? $_('export.placedTitle') : $_('export.title')}
      </h3>
      <button
        type="button"
        onclick={onClose}
        disabled={placing}
        class="rounded-control p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
      >
        <span class="sr-only">{$_('buttons.close')}</span>
        <X class="h-4 w-4" />
      </button>
    </div>

    <div class="px-5 py-4">
      {#if asking}
        <p class="flex items-center gap-2 py-6 text-sm text-gray-500">
          <Loader2 class="h-4 w-4 animate-spin" />
          {$_('export.pricing')}
        </p>
      {:else if placed}
        <!--
          What happens next, which differs by billing mode and is the only
          thing the customer still needs to know.
        -->
        <div class="flex items-start gap-3 py-2">
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-50 text-accent-600"
          >
            {#if placed.delivered}
              <Check class="h-5 w-5" />
            {:else}
              <Mail class="h-5 w-5" />
            {/if}
          </span>
          <div>
            <p class="text-sm font-medium text-gray-900">
              {placed.delivered ? $_('export.deliveredTitle') : $_('export.invoicedTitle')}
            </p>
            <p class="mt-1 text-sm text-gray-600">
              {placed.delivered ? $_('export.deliveredBody') : $_('export.invoicedBody')}
            </p>
            <p class="mt-2 text-xs text-gray-500">
              {$_('export.reference', { values: { id: placed.order_id } })}
            </p>
          </div>
        </div>
      {:else if quote}
        <p class="mb-3 text-sm text-gray-600">
          {$_('export.rows', { values: { count: quote.rows } })}
        </p>

        <!--
          The lines as the invoice will read them, including the discount as
          its own negative line. An invoice showing a lower tier price would be
          a different price list rather than a discount, and the customer has
          to see both what the rows cost and what they were let off.
        -->
        <table class="w-full text-sm">
          <tbody class="divide-y divide-hairline">
            {#each quote.lines as line (line.description)}
              <tr>
                <td class="py-1.5 pr-3 text-gray-700">{line.description}</td>
                <td class="py-1.5 pr-3 text-right tabular-nums text-gray-500">{line.quantity}</td>
                <td class="py-1.5 text-right tabular-nums text-gray-900">{money(line.total)}</td>
              </tr>
            {/each}
          </tbody>
          <tfoot class="border-t border-hairline">
            <tr>
              <td colspan="2" class="py-1.5 pr-3 text-right text-gray-500">{$_('export.net')}</td>
              <td class="py-1.5 text-right tabular-nums text-gray-700">{money(quote.net)}</td>
            </tr>
            <tr>
              <!-- VAT is computed from the reduced net: tax is due on the
                   consideration, so a discount reduces the base and the tax
                   follows. -->
              <td colspan="2" class="py-1.5 pr-3 text-right text-gray-500">{$_('export.vat')}</td>
              <td class="py-1.5 text-right tabular-nums text-gray-700">{money(quote.vat)}</td>
            </tr>
            <tr>
              <td colspan="2" class="py-1.5 pr-3 text-right font-semibold text-gray-900">
                {$_('export.gross')}
              </td>
              <td class="py-1.5 text-right text-base font-semibold tabular-nums text-gray-900">
                {money(quote.gross)}
              </td>
            </tr>
          </tfoot>
        </table>

        <!--
          What happens on confirming, which is the thing that differs by billing
          mode and the thing a customer is deciding about.

          Postpaid delivers immediately and bills afterwards — the note used to
          say only that it would be invoiced, which left the impression that the
          data waited on the invoice as it does for prepaid. It does not.
        -->
        <p class="mt-3 text-xs text-gray-500">
          {quote.billing_mode === 'postpaid' ? $_('export.postpaidNote') : $_('export.prepaidNote')}
        </p>

        <!--
          It cannot be undone, and that is said before the button rather than
          after it. An export is a copy leaving the app; there is nothing to
          take back once it has.
        -->
        <p
          class="mt-3 flex items-start gap-2 rounded-control bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          <AlertTriangle class="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {$_('export.irreversible')}
        </p>
      {/if}

      {#if refusal}
        <p class="mt-3 text-sm text-red-600">{refusal}</p>
      {/if}
    </div>

    <div class="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
      {#if placed}
        <button
          type="button"
          onclick={onClose}
          class="rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
        >
          {$_('buttons.gotIt')}
        </button>
      {:else}
        <button
          type="button"
          onclick={onClose}
          disabled={placing}
          class="rounded-control px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-40"
        >
          {$_('buttons.cancel')}
        </button>
        <button
          type="button"
          onclick={place}
          disabled={placing || !quote || quote.rows === 0}
          class="inline-flex items-center gap-2 rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
        >
          {#if placing}
            <Loader2 class="h-4 w-4 animate-spin" />
          {/if}
          {$_('export.confirm')}
        </button>
      {/if}
    </div>
  </div>
</div>
